import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { apiRequest } from "../utils/http";

const formatCurrency = (value) => `NGN ${Number(value || 0).toLocaleString("en-NG")}`;

const SalesPage = () => {
  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [error, setError] = useState("");

  const loadSales = async (searchTerm = "") => {
    try {
      setError("");
      const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const response = await apiRequest(`/api/quotations/sales${query}`);
      setSales(response?.data || []);
    } catch (loadError) {
      setError(loadError.message || "Unable to load sales.");
    }
  };

  useEffect(() => {
    loadSales();
  }, []);

  const totalRevenue = useMemo(
    () => sales.reduce((sum, sale) => sum + Number(sale.subtotal || 0), 0),
    [sales]
  );

  const handleSearch = async (event) => {
    event.preventDefault();
    const nextSearch = search.trim();
    setAppliedSearch(nextSearch);
    await loadSales(nextSearch);
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ mb: 1 }}>Sales Record</Typography>
        <Typography sx={{ color: "text.secondary" }}>
          View only quotations with confirmed payment. This page is restricted to super admins.
        </Typography>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h5">{sales.length}</Typography>
          <Typography sx={{ color: "text.secondary" }}>Confirmed sales</Typography>
        </Paper>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h5">{formatCurrency(totalRevenue)}</Typography>
          <Typography sx={{ color: "text.secondary" }}>Total confirmed revenue</Typography>
        </Paper>
      </Stack>

      <Paper sx={{ p: 2.5 }}>
        <Box component="form" onSubmit={handleSearch}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Search sales"
              placeholder="Quotation ID, customer email, or phone number"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button type="submit" variant="contained" startIcon={<SearchRoundedIcon />} sx={{ minWidth: 180 }}>
              Search
            </Button>
          </Stack>
        </Box>
      </Paper>

      {appliedSearch && (
        <Typography sx={{ color: "text.secondary" }}>
          Showing results for "{appliedSearch}"
        </Typography>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Paper sx={{ overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Quotation ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment Confirmed</TableCell>
              <TableCell>Confirmed By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sales.map((sale) => (
              <TableRow key={sale._id} hover>
                <TableCell>{sale.quoteNumber || "Quotation"}</TableCell>
                <TableCell>{sale.customer?.fullName || "Customer"}</TableCell>
                <TableCell>{sale.customer?.email || "Not provided"}</TableCell>
                <TableCell>{sale.customer?.phone || "Not provided"}</TableCell>
                <TableCell>{sale.totalItems || 0}</TableCell>
                <TableCell>{formatCurrency(sale.subtotal)}</TableCell>
                <TableCell>
                  {sale.paymentConfirmedAt ? new Date(sale.paymentConfirmedAt).toLocaleString() : "Not confirmed"}
                </TableCell>
                <TableCell>{sale.paymentConfirmedBy?.fullName || sale.paymentConfirmedBy?.email || "Admin"}</TableCell>
              </TableRow>
            ))}

            {sales.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography sx={{ py: 2, color: "text.secondary" }}>
                    No confirmed sales found yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

export default SalesPage;
